<script lang="ts">
	import type { CardsBlock } from '$models/Block/CardsBlock';
	import Button from '$components/UI/Buttons/Button.svelte';
	import SliderWrapper from '$components/UI/Slider/SliderWrapper.svelte';
	import { getWordPressImageSrcSet } from '$lib/images';
	import { sanitizeHtml } from '$lib/utils';

	interface Props {
		block: CardsBlock;
	}

	const { block }: Props = $props();
</script>

{#if block.introText}
	<div class="block-intro">
		{@html block.introText}
	</div>
{/if}

<div class="cards cards--count-{block.cards.length}">
	<SliderWrapper options={{ loop: false, align: 'start', inViewThreshold: 1 }} nav="dots">
		{#snippet slides(Slide)}
			{#each block.cards as card, index}
				<Slide>
					<div class="card">
						{#if card.image}
							<figure class="card-image">
								<img
									src={card.image.url}
									srcset={getWordPressImageSrcSet(card.image)}
									sizes="(min-width: 1280px) 248px, (min-width: 768px) 33vw, (min-width: 640px) 50vw, 80vw"
									alt={card.image.alt}
									width={card.image.width}
									height={card.image.height}
									loading="lazy"
									decoding="async"
								/>
							</figure>
						{/if}
						<div class="card-content">
							{#if card.title}
								<h3 class="card-title">{@html card.title}</h3>
							{/if}

							{#if card.text}
								<div class="card-text rich-text">
									{@html sanitizeHtml(card.text)}
								</div>
							{/if}

							{#if card.button}
								<div class="card-button button--{card.buttonClass}">
									<Button type="anchor" href={card.button.url}>
										{card.button.title}
									</Button>
								</div>
							{/if}
						</div>
					</div>
				</Slide>
			{/each}
		{/snippet}
	</SliderWrapper>
</div>

<style lang="postcss" global>
	.cards {
		&:not(:last-child) {
			margin-bottom: var(--spacing-10);

			@media (--lg) {
				margin-bottom: var(--spacing-12);
			}
		}

		.slider {
			@media (--sm) {
				--slide-size: calc(100% / 2);
			}

			@media (--md) {
				--slide-size: calc(100% / 3);
			}

			@media (--lg) {
				--slide-size: 25%;
			}
		}

		/* modifiers */

		&--count-2 {
			.slider {
				--slide-size: calc(100% / 2);
				@media (--md) {
					--slide-size: calc(100% / 3);
				}

				@media (--lg) {
					--slide-size: 25%;
				}
			}
		}

		&--count-2,
		&--count-3 {
			.slider {
				&-container {
					@media (--sm) {
						justify-content: center;
					}
				}
			}
		}

		&--count-4 {
			.slider {
				@media (--sm) {
					--slide-size: 50%;
				}

				@media (--lg) {
					--slide-size: 25%;
				}
			}
		}

		&--count-5 {
			.slider {
				@media (--md) {
					--slide-size: calc(100% / 4);
				}
				@media (--lg) {
					--slide-size: calc(100% / 5);
				}
			}
		}

		&--count-6,
		&--count-7,
		&--count-8,
		&--count-9,
		&--count-10 {
			.slider {
				@media (--md) {
					--slide-size: calc(100% / 4);
				}
				@media (--lg) {
					--slide-size: calc(100% / 5);
				}
				@media (--xl) {
					--slide-size: calc(100% / 6);
				}
			}
		}
	}

	.card {
		& {
			flex: 1;
			display: flex;
			flex-direction: column;
		}
		&-image {
			max-width: 248px;
			margin: 0 auto var(--spacing-6);
			img {
				display: block;
			}
		}

		&-content {
			flex: 1;
			display: flex;
			flex-direction: column;
			text-align: center;
		}

		&-title {
			text-transform: var(--cards-title-text-tranform);
			font-family: var(--cards-title-font-family);
			font-size: var(--cards-title-font-size);
			font-weight: var(--cards-title-font-weight);
			line-height: var(--font-lineheight-1);
			margin-bottom: var(--spacing-2);
		}

		&-text {
			display: flex;
			flex-direction: column;
			justify-content: space-between;
			flex: 1;
			font-size: var(--cards-text-font-size);

			@media (--sm) {
				max-width: 85%;
				margin: 0 auto;
			}

			&:not(:last-child) {
				margin-bottom: var(--spacing-4);
			}
		}

		&-button {
			margin-top: auto;
		}

		/* boxed */

		&--boxed {
			background-color: white;
			padding: var(--spacing-4);
		}
	}
</style>
